// src/core/api-client.ts
import { CONFIG } from '../config';

const DEVICE_ID = 'fb2e3a7e-63bc-43d1-8bd9-3a215bb5644a';

function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    accept: 'text/plain, application/json',
    deviceid: DEVICE_ID,
    'X-Client-Type': 'Blazor',
  };

  const token = localStorage.getItem('accessToken');

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

async function handleUnauthorized(response: Response): Promise<boolean> {
  if (response.status !== 401) {
    return false;
  }
  await logoutSharedSession();
  // Xóa token hiện tại
  localStorage.removeItem('accessToken');

  // Chuyển về trang login
  window.location.href = '/#/login';

  return true;
}

async function parseResponse(response: Response): Promise<any> {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  return await response.text();
}

function getErrorMessage(
  response: Response,
  resData: any
): string {
  const defaultMessage = `Lỗi hệ thống (${response.status})`;

  if (typeof resData === 'string') {
    return resData || defaultMessage;
  }

  if (resData) {
    return (
      resData?.error?.message ||
      resData?.error?.details ||
      resData?.message ||
      defaultMessage
    );
  }

  return defaultMessage;
}

function unwrapResult<T>(resData: any): T {
  // ABP trả về:
  // {
  //   result: {...}
  // }
  if (
    resData &&
    typeof resData === 'object' &&
    'result' in resData
  ) {
    return resData.result as T;
  }

  return resData as T;
}
export async function requestApi<T = any>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: any
): Promise<T> {
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${CONFIG.API_BASE_URL}${endpoint}`;

  const headers = getHeaders();

  const config: RequestInit = {
    method,
    headers,
  };

  if (body !== undefined && body !== null) {
    // 👉 Thêm kiểm tra nếu body là FormData thì bỏ qua json
    if (body instanceof FormData) {
      delete headers['Content-Type']; // Xóa nếu lỡ getHeaders() có gán mặc định
      config.body = body;
    } else {
      headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify(body);
    }
  }

  const response = await fetch(url, config);

  // ============================
  // 401 - Unauthorized
  // ============================
  if (await handleUnauthorized(response)) {
    return new Promise<T>(() => {});
  }

  const resData = await parseResponse(response);

  // ============================
  // Error
  // ============================
  if (!response.ok) {
    const errorMsg = getErrorMessage(response, resData);
    throw new Error(errorMsg);
  }

  // ============================
  // Success
  // ============================
  return unwrapResult<T>(resData);
}
export async function logoutSharedSession() {
    await fetch(
        `${CONFIG.API_BASE_URL}/api/app/auth-session/logout`,
        {
            method: 'POST',
            credentials: 'include'
        }
    );

    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
}

export async function uploadApi<T = any>(
  endpoint: string,
  formData: FormData
): Promise<T> {
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${CONFIG.API_BASE_URL}${endpoint}`;

  const headers = getHeaders();

  // QUAN TRỌNG:
  // Không set Content-Type ở đây.
  // Browser sẽ tự tạo:
  // multipart/form-data; boundary=...
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  // ============================
  // 401 - Unauthorized
  // ============================
  if (await handleUnauthorized(response)) {
    // Không tiếp tục xử lý response sau khi redirect
    return new Promise<T>(() => {});
  }

  const resData = await parseResponse(response);

  // ============================
  // Error
  // ============================
  if (!response.ok) {
    const errorMsg = getErrorMessage(response, resData);
    throw new Error(errorMsg);
  }

  // ============================
  // Success
  // ============================
  return unwrapResult<T>(resData);
}